import { ChangeEvent, createContext, useContext, useMemo } from 'react';
import { usePosMappings } from '../hooks/usePosMappings';
import { GetPosMappingResponse, GetPaperPackagingResponse } from '../../../app/services/api/generated';
import { TFunction } from 'i18next';
import { MappingType } from '../enums/MappingType';

export interface PosMappingsValues {
  t: TFunction<'posMapping', undefined>;
  tabs: { key: MappingType; label: string }[];
  activeTab: number;
  setActiveTab: (i: number) => void;
  onChange: (_: unknown, i: number) => void;
   totalCount: number,
   posMappings: GetPosMappingResponse[],
   paperPackagingMappings: GetPaperPackagingResponse[],
   handleSort: (property: string) => void,
   order: 'asc' | 'desc',
   orderBy: string,
   skuSearch: string,
   storeSearch: string,
   itemType: number | undefined,
   paperPackagingType: number | undefined,
   handleStoreSearchChange: (event: ChangeEvent<HTMLInputElement>) => void,
   handleSkuSearchChange: (event: ChangeEvent<HTMLInputElement>) => void,
   handleItemTypeChange: (newItemType: number | undefined) => void,
   handlePaperPackagingTypeChange: (newPaperPackagingType: number | undefined) => void,
   searchPOSMapping: () => void,
   clearAll: () => void,
    posMappingDelete: (entity: GetPosMappingResponse | GetPaperPackagingResponse | undefined) => void,
    edit: (entity: GetPosMappingResponse | GetPaperPackagingResponse | undefined) => void,
    openCreateModal: () => void,
    closeCreateModal: () => void,
    closeEditModal: () => void,
    handleDelete: () => void,
    handleCloseModal: () => void,
    selectedEntity: GetPosMappingResponse | GetPaperPackagingResponse | undefined,
    showEditModal: boolean,
    showCreateModal: boolean,
    showDeleteModal: boolean,
    setShowDeleteModal: (show: boolean) => void,
    showResultMessage: { open: boolean; message: string; severity: 'error' | 'warning' | 'info' | 'success' },
    setShowResultMessage: (msg: { open: boolean; message: string; severity: 'error' | 'warning' | 'info' | 'success' }) => void,
    page: number,
    setPage: (page: number) => void,
    maxPages: number,
    isLoading: boolean, 
}

export const PosMappingsContext = createContext<PosMappingsValues | null>(null);

export const PosMappingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { 
    t,
    tabs, 
    activeTab,
    setActiveTab, 
    onChange,
    totalCount,
    posMappings,
    paperPackagingMappings,
    handleSort, 
    order,
    orderBy,
    skuSearch,
    storeSearch,
    itemType,
    paperPackagingType,
    handleStoreSearchChange,
    handleSkuSearchChange,
    handleItemTypeChange,
    handlePaperPackagingTypeChange,
    searchPOSMapping,
    clearAll,
    posMappingDelete,
    edit,
    openCreateModal,
    closeCreateModal,
    closeEditModal,
    handleDelete,
    handleCloseModal,
    selectedEntity,
    showEditModal,
    showCreateModal,
    showDeleteModal,
    setShowDeleteModal,
    showResultMessage,
    setShowResultMessage,
    page,
    setPage,
    maxPages,
    isLoading, 
  } = usePosMappings();

  const value = useMemo(
    () => ({
      t,
      tabs,
      activeTab,
      setActiveTab,
      onChange,
      totalCount,
      posMappings,
      paperPackagingMappings,
      handleSort,
      order,
      orderBy,
      skuSearch,
      storeSearch,
      itemType,
      paperPackagingType,
      handleStoreSearchChange,
      handleSkuSearchChange,
      handleItemTypeChange,
      handlePaperPackagingTypeChange,
      searchPOSMapping,
      clearAll,
      posMappingDelete,
      edit,
      openCreateModal,
      closeCreateModal,
      closeEditModal,
      handleDelete,
      handleCloseModal,
      selectedEntity,
      showEditModal,
      showCreateModal,
      showDeleteModal,
      setShowDeleteModal,
      showResultMessage,
      setShowResultMessage,
      page,
      setPage,
      maxPages,
      isLoading, 
    }),
    [tabs, activeTab, setActiveTab, onChange, totalCount, posMappings, paperPackagingMappings, 
      handleSort, order, orderBy, skuSearch, storeSearch, itemType, paperPackagingType,
      handleStoreSearchChange, handleSkuSearchChange, handleItemTypeChange, handlePaperPackagingTypeChange,
      searchPOSMapping, clearAll, posMappingDelete, edit, openCreateModal, closeCreateModal, closeEditModal, handleDelete, handleCloseModal, selectedEntity, showEditModal, 
      showCreateModal, showDeleteModal, setShowDeleteModal, showResultMessage, setShowResultMessage, page, setPage, maxPages, isLoading],
  );

  return <PosMappingsContext.Provider value={value}>{children}</PosMappingsContext.Provider>;
};

export const usePosMappingsContext = () => {
  const ctx = useContext(PosMappingsContext);
  if (!ctx) throw new Error('usePosMappingsContext must be used within PosMappingsProvider');
  return ctx;
};
